import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { NavigationButton, NavigationButtonGroup } from 'web-ui-components/lib/navigation/buttons';
import { ServicingSection } from 'web-ui-components/lib/global/layout';
import { BrandedPanel } from 'web-ui-components/lib/layout/content';
import { Inline } from 'web-ui-components/lib/layout/grids';
import { PageHeading, SubsectionHeading } from 'web-ui-components/lib/typography/headings';
import { StandardBody } from 'web-ui-components/lib/typography/body';
import { StandardLabel } from 'web-ui-components/lib/typography/labels';
import { StatusTag } from 'web-ui-components/lib/typography/tags';
import { getAccountName, getCopyText, getReasonCode, getRef } from '../../states/selectors';
import withContent from '../../../containers/withContent';
import copContent from '../Content/cop.content';
import { goToCOPPage } from '../../states/actions';
import { pages } from '../../../states/page/actions';
import { copReasonCodes } from '../../../config';

export function NoMatch({
	content,
	copyText,
	handleCancel,
	handleEdit,
	goToCOPPage,
	reasonCode,
	name,
	reference,
	payeeId,
	setKeepAsIs,
}) {
	const {
		editButtonText,
		keepButtonText,
		cancelButtonText,
		nameEnteredLabel,
		refEnteredLabel = '',
		retryButtonText,
	} = content;

	const { heading, header_detail, footer, footer_detail } = copyText;
	const showKeepAsIs = !(reasonCode === copReasonCodes.noMatch.AC01);

	const editButton =
		reasonCode === copReasonCodes.noMatch.SCNS ? (
			<NavigationButton.Secondary
				data-test-id="no-match-edit-button"
				labelText={editButtonText}
				onClick={() => handleEdit(payeeId)}
				key={'editButton'}
			/>
		) : (
			<NavigationButton.Primary
				data-test-id="no-match-edit-button"
				labelText={editButtonText}
				onClick={() => handleEdit(payeeId)}
			/>
		);
	const keepAsIsButton = showKeepAsIs && (
		<NavigationButton.Secondary
			data-test-id="no-match-keep-as-is-button"
			labelText={keepButtonText}
			onClick={() => {
				setKeepAsIs(true);
				goToCOPPage(pages.COP.AccountWarning);
			}}
			key={'keepAsIs'}
		/>
	);
	const cancelButton = (
		<NavigationButton.Tertiary
			data-test-id="no-match-cancel-button"
			labelText={cancelButtonText}
			onClick={handleCancel}
		/>
	);
	const retryButton = (
		<NavigationButton.Primary
			data-test-id="no-match-retry-button"
			labelText={retryButtonText}
			onClick={() => goToCOPPage(pages.COP.MakeMatch)}
		/>
	);
	let primaryButton;
	const secondaryButtons = [keepAsIsButton];
	if (reasonCode === copReasonCodes.noMatch.SCNS) {
		primaryButton = retryButton;
		secondaryButtons.push(editButton);
	} else {
		primaryButton = editButton;
	}
	return (
		<ServicingSection>
			<PageHeading data-test-id="no-match">{heading}</PageHeading>
			<p>{header_detail}</p>
			{reasonCode === copReasonCodes.noMatch.ANNM && (
				<BrandedPanel>
					<StandardLabel>
						<span>{nameEnteredLabel}</span>
					</StandardLabel>
					<Inline.Justified
						nodes={[
							<StandardBody>
								<span>{name}</span>
							</StandardBody>,
							<StatusTag.ErrorMajor>No match</StatusTag.ErrorMajor>,
						]}
					/>
				</BrandedPanel>
			)}
			{reasonCode === copReasonCodes.noMatch.IVCR && (
				<BrandedPanel>
					<StandardLabel>
						<span>{refEnteredLabel}</span>
					</StandardLabel>
					<Inline.Justified
						nodes={[
							<StandardBody>
								<span>{reference}</span>
							</StandardBody>,
							<StatusTag.ErrorMajor>No match</StatusTag.ErrorMajor>,
						]}
					/>
				</BrandedPanel>
			)}
			<SubsectionHeading>{footer}</SubsectionHeading>
			<p>{footer_detail}</p>
			<NavigationButtonGroup
				primaryButton={primaryButton}
				secondaryButtons={secondaryButtons}
				tertiaryButtons={cancelButton}
			/>
		</ServicingSection>
	);
}

NoMatch.propTypes = {
	content: PropTypes.shape({}),
	handleCancel: PropTypes.func,
	handleEdit: PropTypes.func,
	goToCOPPage: PropTypes.func.isRequired,
	copyText: PropTypes.shape({}),
	reasonCode: PropTypes.string,
	name: PropTypes.string,
	ref: PropTypes.string,
};

function mapStateToProps(state) {
	return {
		copyText: getCopyText(state),
		reasonCode: getReasonCode(state),
		name: getAccountName(state),
		reference: getRef(state),
	};
}

const connectWrapper = connect(mapStateToProps, { goToCOPPage });
const contentWrapper = withContent({ content: copContent });

export default compose(connectWrapper, contentWrapper)(NoMatch);
